import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, retry, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  Skeleton,
  SkeletonClone,
  CloneRequest,
  GapAnalysisReport,
  GapAnalysisResult
} from '../models/skeleton.model';

@Injectable({
  providedIn: 'root'
})
export class SkeletonService {
  private http: HttpClient;
  private api = `${environment.apiUrl}/skeletons`;

  // Signals for state management
  isLoading = signal<boolean>(false);
  errorMessage = signal<string | null>(null);

  constructor(http?: HttpClient) {
    this.http = http || inject(HttpClient);
  }

  /**
   * List all skeletons with optional filtering
   * @param module Optional module filter
   * @param testType Optional test type filter
   * @returns Promise resolving to array of Skeleton
   */
  async listSkeletons(module?: string, testType?: string): Promise<Skeleton[]> {
    try {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      const params: any = {};
      if (module) params.module = module;
      if (testType) params.testType = testType;

      const result = await firstValueFrom(
        this.http.get<any>(this.api, { params }).pipe(
          retry(2),
          catchError(error => {
            const errorMsg = error?.error?.message || 'Failed to fetch skeletons';
            this.errorMessage.set(errorMsg);
            console.error('Error fetching skeletons:', error);
            return of({ data: [] });
          })
        )
      );

      return Array.isArray(result) ? result : (result?.data || []);
    } catch (error) {
      console.error('Unexpected error in listSkeletons:', error);
      return [];
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Get a single skeleton by ID
   * @param id Skeleton ID
   * @returns Promise resolving to Skeleton
   */
  async getSkeleton(id: string): Promise<Skeleton | null> {
    try {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      const result = await firstValueFrom(
        this.http.get<Skeleton>(`${this.api}/${id}`).pipe(
          retry(1),
          catchError(error => {
            const errorMsg = error?.error?.message || 'Failed to fetch skeleton';
            this.errorMessage.set(errorMsg);
            console.error('Error fetching skeleton:', error);
            throw error;
          })
        )
      );

      return result;
    } catch (error) {
      return null;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Create a new skeleton
   * @param data Skeleton data
   * @returns Promise resolving to created Skeleton
   */
  async createSkeleton(data: Omit<Skeleton, 'id'>): Promise<Skeleton> {
    try {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      const result = await firstValueFrom(
        this.http.post<Skeleton>(this.api, data).pipe(
          retry(1),
          catchError(error => {
            const errorMsg = error?.error?.message || 'Failed to create skeleton';
            this.errorMessage.set(errorMsg);
            throw error;
          })
        )
      );

      return result;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Clone a skeleton for a client
   * @param skeletonId Skeleton ID to clone
   * @param cloneData Clone request data
   * @returns Promise resolving to created SkeletonClone
   */
  async cloneSkeleton(skeletonId: string, cloneData: CloneRequest): Promise<SkeletonClone> {
    try {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      const result = await firstValueFrom(
        this.http.post<SkeletonClone>(
          `${this.api}/${skeletonId}/clone`,
          cloneData
        ).pipe(
          retry(1),
          catchError(error => {
            const errorMsg = error?.error?.message || 'Failed to clone skeleton';
            this.errorMessage.set(errorMsg);
            throw error;
          })
        )
      );

      return result;
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * List all clones of a skeleton
   * @param skeletonId Skeleton ID
   * @returns Promise resolving to array of SkeletonClone
   */
  async listClones(skeletonId: string): Promise<SkeletonClone[]> {
    try {
      this.isLoading.set(true);
      this.errorMessage.set(null);

      const result = await firstValueFrom(
        this.http.get<any>(`${this.api}/${skeletonId}/clones`).pipe(
          retry(1),
          catchError(error => {
            const errorMsg = error?.error?.message || 'Failed to fetch clones';
            this.errorMessage.set(errorMsg);
            console.error('Error fetching clones:', error);
            return of({ data: [] });
          })
        )
      );

      return Array.isArray(result) ? result : (result?.data || []);
    } catch (error) {
      return [];
    } finally {
      this.isLoading.set(false);
    }
  }

  async deleteClone(cloneId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${environment.apiUrl}/clones/${cloneId}`)
    );
  }

  clearError(): void {
    this.errorMessage.set(null);
  }
}
